import { useEffect, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ViewListIcon from "@mui/icons-material/ViewList";
import { Box, Button, IconButton, MenuItem, TextField, Tooltip } from "@mui/material";
import { EditorSection, isSectionEmpty, sectionDisplayName } from "./sectionUtils";
import QuoteSectionsModal from "./QuoteSectionsModal";
import "./QuoteSectionBar.scss";

type Props = {
  sections: EditorSection[];
  activeIndex: number;
  editable: boolean;
  onActiveChange: (index: number) => void;
  onNameChange: (index: number, name: string) => void;
  onAdd: () => void;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
};

const QuoteSectionBar = ({ sections, activeIndex, editable, onActiveChange, onNameChange, onAdd, onMove, onRemove }: Props) => {
  const [manageOpen, setManageOpen] = useState(false);
  const safeIndex = Math.min(activeIndex, sections.length - 1);
  const active = sections[safeIndex];
  // Issue 43: the field is locally uncontrolled so a keystroke never touches the page's
  // `sections` state (and so never re-renders the line-item grid). It re-seeds from the active
  // section's committed name when the active section changes, and pushes edits up on blur and on
  // a short pause in typing (debounced) so Manage Sections and the dirty check see them promptly.
  const [draft, setDraft] = useState(active?.name ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    setDraft(active?.name ?? "");
  }, [active?.key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => window.clearTimeout(debounceRef.current), []);
  if (!active) return null;

  const changeName = (value: string) => {
    setDraft(value);
    window.clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onNameChange(safeIndex, value), 300);
  };
  const commitName = (value: string) => {
    window.clearTimeout(debounceRef.current);
    onNameChange(safeIndex, value);
  };

  return <Box className="quote-section-bar">
    <Box className="quote-section-switch">
      <Tooltip enterDelay={1800} title="Previous section"><span><IconButton size="small" aria-label="Previous section"
        disabled={safeIndex === 0} onClick={() => onActiveChange(safeIndex - 1)}><ChevronLeftIcon /></IconButton></span></Tooltip>
      <TextField className="quote-section-select quick-quote-select" select size="small" value={safeIndex}
        inputProps={{ "aria-label": "Jump to section" }} onChange={event => onActiveChange(Number(event.target.value))}>
        {/* "(Empty)" is useful here - picking blind, before the grid for that section is on
            screen - but redundant once you're actually looking at that (obviously empty) grid,
            so it isn't repeated below for the active section. */}
        {sections.map((section, index) => <MenuItem key={section.key} value={index}>
          {sectionDisplayName(section.name, index)}{isSectionEmpty(section) ? " (Empty)" : ""}
        </MenuItem>)}
      </TextField>
      <Tooltip enterDelay={1800} title="Next section"><span><IconButton size="small" aria-label="Next section"
        disabled={safeIndex === sections.length - 1} onClick={() => onActiveChange(safeIndex + 1)}><ChevronRightIcon /></IconButton></span></Tooltip>
    </Box>

    {editable ? <>
      <TextField className="quote-section-name" size="small" label="Section name" value={draft}
        placeholder={`Option ${safeIndex + 1}`} InputLabelProps={{ shrink: true }}
        inputProps={{ maxLength: 200, "aria-label": "Section name" }}
        helperText={draft.trim() ? `${draft.length}/200` : "Blank uses the default name"}
        onChange={event => changeName(event.target.value)}
        onBlur={event => commitName(event.target.value)} />
      {/* Adding and removing a section both already live in the Sections modal (its own Add
          Section button, and a per-row remove with its own confirm dialog) - duplicating either
          one here too was just two ways to do the same thing. */}
      <Box className="quote-section-actions">
        <Tooltip enterDelay={1800} title="Rename, reorder, add, or remove sections">
          <Button startIcon={<ViewListIcon />} onClick={() => setManageOpen(true)}>Sections</Button>
        </Tooltip>
      </Box>
    </> : <span className="quote-section-name-readonly">{sectionDisplayName(active.name, safeIndex)}</span>}

    <QuoteSectionsModal open={manageOpen} sections={sections} activeIndex={safeIndex}
      onClose={() => setManageOpen(false)} onJump={onActiveChange} onMove={onMove} onAdd={onAdd} onRemove={onRemove} />
  </Box>;
};

export default QuoteSectionBar;
